// Workflow model — per-task-type state machines that hand a task from one
// role to the next (planner → developer ⇄ tester → human review → done).
//
// Goblic is the source of truth for a task's workflow state; external
// systems (Plane) only mirror it. Hand-off between agents happens through
// states (state → role), never through agent-to-agent delegation.
//
// The structured shape here is what the API stores (normalized) in
// `project_policies.workflow` and what `project-policy.yaml`'s `workflows:`
// section parses into. It is deliberately editor-friendly: every state
// carries optional `ui` layout metadata reserved for a future graphical
// editor, which the engine ignores.

/** What happens when a task enters a state. */
export type WorkflowStateKind = 'agent' | 'human' | 'terminal' | 'decision' | 'subtasks'

/** How a terminal state ends the task. */
export type WorkflowOutcome = 'success' | 'failure' | 'cancelled'

export const WORKFLOW_STATE_KINDS: readonly WorkflowStateKind[] = ['agent', 'human', 'terminal', 'decision', 'subtasks']
/** Gate conditions a state's `requires`/`blocks` may reference; the API's default resolver understands exactly these. */
export const WORKFLOW_CONDITIONS: readonly { value: string; label: string }[] = [
  { value: 'agent-eligible', label: 'The task has a desired agent or role' },
  { value: 'has-acceptance-criteria', label: 'The task has acceptance criteria' },
]

export const WORKFLOW_OUTCOMES: readonly WorkflowOutcome[] = ['success', 'failure', 'cancelled']

/** Editor-only layout position of a state; the workflow engine ignores it. */
export interface WorkflowStateLayout {
  x: number
  y: number
}

/**
 * Model level a decision can set for the rest of a task (docs/plans/
 * 2026-10-02-beslisstap.md §7). Translated per executor connection:
 * claude_cli defaults to haiku / sonnet / opus, other connections use
 * `credentials.scope.modelTiers`.
 */
export type ModelTier = 'light' | 'standard' | 'heavy'
export const MODEL_TIERS: readonly ModelTier[] = ['light', 'standard', 'heavy']

/** `nextWorkflow.state`: put the task in the target stream's queue instead of a state. */
export const WORKFLOW_INBOX_STATE = '@inbox'
/** `nextWorkflow.workflow`: the project's default workflow. */
export const DEFAULT_WORKFLOW_REF = '@default'

/** Where a decision option leads when it switches the task to another workflow of the same project. */
export interface WorkflowDecisionSwitch {
  /** `WorkflowDefinition.key` of the target workflow (same project), or `DEFAULT_WORKFLOW_REF` ('@default'): the project's default workflow. */
  workflow: string
  /** State to enter there; defaults to that workflow's `initialState`. `WORKFLOW_INBOX_STATE` ('@inbox') puts the task in the target stream's queue instead. */
  state?: string | null
}

/** One answer of a decision step. Exactly one of `next` / `nextWorkflow` is set. */
export interface WorkflowDecisionOption {
  /** Key the agent answers with; `^[a-z0-9_-]{1,32}$`, unique within the step. Also the variable's value. */
  value: string
  /** What this answer means; goes verbatim into the decision prompt. */
  description?: string | null
  /** Next state in the current workflow (may be a terminal state: a different exit). */
  next?: string | null
  /** Or: continue in another workflow of the project. */
  nextWorkflow?: WorkflowDecisionSwitch | null
  /** Effects on the task, applied in the same transaction as the move. */
  set?: WorkflowDecisionEffects | null
}

export interface WorkflowDecisionEffects {
  /** Model level for every later run of the task (until another decision changes it); null clears it. */
  modelTier?: ModelTier | null
  /** Extra workflow variables to set (name → value), next to the step's own `variable`. */
  vars?: Record<string, string> | null
}

export interface WorkflowState {
  /** Unique within the workflow; also the display name. */
  name: string
  kind: WorkflowStateKind
  /** kind=agent: role name (matched case-insensitively against `roles.name`). */
  agentRole?: string | null
  /**
   * What this step asks, on top of the role's own instructions: for an
   * agent step it goes into the run prompt, for a human step it's shown
   * with the review. Applies only to this workflow, unlike a role change.
   */
  instructions?: string | null
  /** Gate conditions (existing policy-engine semantics). */
  requires: string[]
  blocks: string[]
  /** The next run gets no summaries of earlier steps (only criteria + the previous step's test report). */
  freshContext?: boolean
  /** kind=agent: run completed. kind=subtasks: every subtask succeeded (or there were none). */
  onSuccess?: string | null
  /** kind=agent: run failed / timed out. kind=subtasks: at least one subtask did not succeed (falls back to onSuccess). */
  onFailure?: string | null
  /** kind=agent: run blocked (default: the task stays in this state, status 'blocked'). */
  onBlocked?: string | null
  /** kind=human: gate approved. */
  onApprove?: string | null
  /** kind=human: gate rejected. */
  onReject?: string | null
  /** Maximum number of times this state may be entered per task. */
  maxRounds?: number | null
  /** kind=terminal: how the task ends. */
  outcome?: WorkflowOutcome
  /**
   * kind=agent|human|decision: whether the task's workflow variables are
   * put into this step's run prompt / review (default true). They stay
   * available through `get_task` either way.
   */
  useVariables?: boolean | null
  /** kind=decision: the question. Task context is added automatically; `{{vars.<name>}}` is substituted. */
  question?: string | null
  /** kind=decision: name of the workflow variable the answer is stored in; `^[a-z][a-z0-9_]{0,31}$`. */
  variable?: string | null
  /** kind=decision: 2–8 answers. */
  options?: WorkflowDecisionOption[] | null
  /** kind=decision: `value` of the option used on failure / timeout / invalid answer. Required. */
  defaultOption?: string | null
  /**
   * kind=subtasks (docs/plans/2026-10-03-subtaken.md): workflow key for
   * subtasks that don't name their own; null = the project's default workflow.
   */
  childWorkflow?: string | null
  /** kind=subtasks: how many subtasks may run at once (1–10, default `DEFAULT_SUBTASKS_MAX_PARALLEL`). */
  maxParallel?: number | null
  /** Reserved for the graphical editor; the engine ignores it. */
  ui?: WorkflowStateLayout | null
}

/** Subtasks running at once when a `subtasks` state sets no `maxParallel`. */
export const DEFAULT_SUBTASKS_MAX_PARALLEL = 2
/** Upper bound of `maxParallel`. */
export const MAX_SUBTASKS_PARALLEL = 10

export interface WorkflowDefinition {
  /** Slug, e.g. 'feature', 'bug'. Unique within a project. */
  key: string
  name: string
  description?: string | null
  initialState: string
  states: WorkflowState[]
  /** Template this workflow was copied from, if any. */
  templateKey?: string | null
}

/** Every workflow of one project, as stored in `project_policies.workflow`. */
export interface ProjectWorkflows {
  defaultWorkflow: string | null
  workflows: WorkflowDefinition[]
}

/** Result of `validateWorkflowDefinition` (validators/index.ts). */
export interface WorkflowValidationResult {
  errors: string[]
  warnings: string[]
}

export type TaskTransitionCause =
  | 'start'
  | 'run_completed'
  | 'run_failed'
  | 'run_blocked'
  | 'approved'
  | 'rejected'
  | 'manual'
  | 'external'
  | 'max_rounds'
  | 'decision'
  | 'subtasks_done'

export const TASK_TRANSITION_CAUSES: readonly TaskTransitionCause[] = [
  'start',
  'run_completed',
  'run_failed',
  'run_blocked',
  'approved',
  'rejected',
  'manual',
  'external',
  'max_rounds',
  'decision',
  'subtasks_done',
]

/** One row of a task's workflow history (`task_transitions`). */
export interface TaskTransition {
  id: string
  taskId: string
  workflowKey: string
  fromState: string | null
  toState: string
  cause: TaskTransitionCause
  runId: string | null
  actorId: string | null
  /** The person who made the move (approve/reject/manual), when there is one. */
  actorName?: string | null
  reason: string | null
  createdAt: string
}

/** Where a template comes from: shipped in code, or saved by a workspace. */
export type WorkflowTemplateSource = 'builtin' | 'workspace'

export interface WorkflowTemplate {
  /** 'feature' | 'bug' | 'hotfix' | 'chore' | 'spike' (built-in); unique per workspace for workspace templates. */
  key: string
  name: string
  description: string
  /** Role names the template's agent states use. */
  requiredRoles: string[]
  definition: WorkflowDefinition
  /** Set by GET /workflow-templates (absent on the in-code constants: those are built-in). */
  source?: WorkflowTemplateSource
  /** Workspace templates only: row id (PUT/DELETE /workflow-templates/:id, from-template `templateId`). */
  id?: string
  /** Workspace templates only. */
  workspaceId?: string
  /** Workspace templates only: bumped on every update. */
  version?: number
  /** Workspace templates only (ISO timestamps). */
  createdAt?: string
  updatedAt?: string
}

/** Body of POST /workflow-templates (a workspace template). */
export interface CreateWorkflowTemplateRequest {
  workspaceId: string
  name: string
  description?: string | null
  /** Slug; defaults to one derived from `name`, made unique within the workspace. */
  key?: string
  definition: WorkflowDefinition
}

/** Body of PUT /workflow-templates/:id (every field optional). */
export interface UpdateWorkflowTemplateRequest {
  name?: string
  description?: string | null
  definition?: WorkflowDefinition
}

// ---------------------------------------------------------------------------
// Editor contract (PUT /projects/:id/workflows, usage, versions)
// ---------------------------------------------------------------------------

/**
 * How tasks of one workflow follow a changed definition (states are
 * identified by name). Keys are state names of the currently active
 * version.
 *  - `renames`: old name → new name; tasks in the old state move along.
 *  - `reassign`: deleted state → state (of the new definition) its active
 *    tasks move to. A deleted state with active tasks and no entry here is
 *    refused (422 `workflow_states_in_use`).
 */
export interface WorkflowStateMigration {
  renames?: Record<string, string>
  reassign?: Record<string, string>
}

/** Per workflow key. */
export type WorkflowMigrations = Record<string, WorkflowStateMigration>

/** Body of PUT /projects/:id/workflows. */
export interface WorkflowsUpdateRequest extends ProjectWorkflows {
  /**
   * The `policyVersion` the edit started from (optimistic locking). When
   * given and different from the active version, the API answers 409
   * `policy_version_conflict`. `null` = "no policy stored yet"; omit to skip
   * the check.
   */
  expectedPolicyVersion?: number | null
  migrations?: WorkflowMigrations
}

/** Error code (409) when `expectedPolicyVersion` is stale; `details: PolicyVersionConflictDetails`. */
export const POLICY_VERSION_CONFLICT_CODE = 'policy_version_conflict'

export interface PolicyVersionConflictDetails {
  currentVersion: number | null
}

/** Error code (422) when deleted states still hold active tasks; `details: WorkflowStatesInUseDetails`. */
export const WORKFLOW_STATES_IN_USE_CODE = 'workflow_states_in_use'

export interface WorkflowStatesInUseDetails {
  /** The deleted state names that still hold active tasks. */
  states: string[]
  /** The same, per workflow, with the tasks concerned (`taskIds` at most 50). */
  entries: { workflowKey: string; state: string; activeTasks: number; taskIds: string[] }[]
}

/** Active tasks in one state (active = the task is not in a terminal state and not completed/cancelled). */
export interface WorkflowStateUsage {
  activeTasks: number
  /** At most 50. */
  taskIds: string[]
}

/** GET /projects/:id/workflows/:key/usage — every state of the workflow (plus any state tasks sit in that no longer exists). */
export interface WorkflowUsage {
  states: Record<string, WorkflowStateUsage>
}

/** One row of GET /projects/:id/workflows/versions (newest first). */
export interface WorkflowVersionSummary {
  version: number
  createdAt: string
  createdBy: string | null
  isActive: boolean
}

/** Every state name a state can move to (its `on*` targets), without duplicates. */
export function workflowStateTargets(state: WorkflowState): string[] {
  const targets = [state.onSuccess, state.onFailure, state.onBlocked, state.onApprove, state.onReject]
  // A decision step leads wherever its options' `next` point (an option that switches workflow has no target here).
  if (state.kind === 'decision') targets.push(...(state.options ?? []).map((o) => o.next))
  return [...new Set(targets.filter((t): t is string => typeof t === 'string' && t.length > 0))]
}

/** Looks up a workflow by key; `key` null/undefined falls back to the project's default workflow. */
export function findWorkflow(
  workflows: ProjectWorkflows,
  key?: string | null,
): WorkflowDefinition | null {
  const wanted = key ?? workflows.defaultWorkflow
  if (!wanted) return null
  return workflows.workflows.find((w) => w.key === wanted) ?? null
}

/** Looks up a state by name within a workflow. */
export function findWorkflowState(def: WorkflowDefinition, name: string): WorkflowState | null {
  return def.states.find((s) => s.name === name) ?? null
}

/** Role names the workflow's agent states use (first spelling wins, compared case-insensitively). */
export function workflowRequiredRoles(def: WorkflowDefinition): string[] {
  const seen = new Set<string>()
  const roles: string[] = []
  for (const state of def.states) {
    const role = state.kind === 'agent' || state.kind === 'decision' ? state.agentRole?.trim() : undefined
    if (!role || seen.has(role.toLowerCase())) continue
    seen.add(role.toLowerCase())
    roles.push(role)
  }
  return roles
}
